
import { api } from "@/auth/api";
import { extract } from "@/commons/businessRules";
import { useApi } from "@/requests/useApi";
import { settings } from "@/settings";
import store from "@/store";
import { ScaleBuisinessRuleSection } from "@/types";
import { formatDateRecursively, getOperator, globalConfig, headers, Masks, operator, Paths, setOtherFilters, UON } from "@/utils";
import { formatDateLocale } from "@/utils/configuration/formatters-config";

export const aideKeys: string[] = [
    'amount',
    'amountMax',
    'complexResultAmount',
    'amountMin',
    'annualRate',
    'annualRateMax',
    'annualRateMin',
    'basis',
    'calculationMethodOption',
    'currency'
]
const aideResults =  {
  amount: "",
  amountMax: "",
  complexResultAmount: "",
  amountMin: "",
  annualRate: "",
  annualRateMax: "",
  annualRateMin: "",
  basis: "",
  calculationMethodOption: "",
  currency: ""
}

export const outputFormAdvanceConfiguration : any = {
  amount: ["amount"]
};

export const aidesFilter = async (filter: any, offset?: any, limit?: any) => {
  const PRODUCT_FEATURE = "product_product_feature";
      const qc_header: any = {
        qc: {
          queryId: "product-feature-search",
          offset: offset || 0,
          limit: limit || 100,
          parameters: {
            ...filter,
          },
        },
      };
    
      const operators:any = [];

        operators.push(
          getOperator(
            "product_product_feature",
            "_discriminator",
            "LIKE",
              globalConfig.formatters.getEqualValue('product-SubsidyProduct'),
            "_discriminator"
          )
  );
  if (filter && filter.name) {
    operators.push(
      getOperator(
        "lower(product_product_feature",
        "_name)",
        "LIKE",
        globalConfig.formatters.getEqualValue('%' + filter.name + '%').toLowerCase(),
        ""))
  }
         if (filter?.from && filter?.from !== "") {
           operators.push(
             operator({
               val: filter.from,
               table: PRODUCT_FEATURE,
               dbField: "validity_from",
             }).GREATER_THAN_EQUAL
           );
         }
       
         if (filter?.validity?.until && filter?.validity?.until !== "") {
           operators.push(
             operator({
               val: filter.until,
               table: PRODUCT_FEATURE,
               dbField: "validity_until",
             }).LESS_THAN_EQUAL
           );
         }
        if(filter?.aide === true){
          operators.push(getOperator("product_product_feature", "validity_from", "ISNOTNULL"));
        }
      
  if (operators.length > 0) {
    qc_header.qc.otherFilters = {
      expressions: [
        {
          and: {
            operators,
          },
        },
      ],
    }
  }

        
      return await api().get(settings.api_query_url, {
              headers: headers({
                  qc: JSON.stringify(qc_header.qc)
              })
          });
}
function filterKeys(obj: any, allowedKeys: any) {
    return Object.fromEntries(
        Object.entries(obj).filter(([key]) => allowedKeys.includes(key))
    );
}
export const saveAide = async (aide: any, daaqResourceUid?: string) => {
    formatDateRecursively(aide.validity, Masks.dateMask_Sys, Masks.dateMask)
    if (aide.accessoryProductRule) {
        (store.state.queryBuilderModule.queryBuilder[ScaleBuisinessRuleSection.AIDE as any] as any).selectedBuisinessRule!.returnMultipleResults = true
        
      delete aide.aideResults
      aide.reference = aide.reference && aide.reference !== '' ? aide.reference : aide.name.replace(/\s+/g, '-').toLowerCase()
        aide.accessoryProductRule.ruleEngineId = null
        aide.accessoryProductRule.rule = (store.state.queryBuilderModule.queryBuilder[ScaleBuisinessRuleSection.AIDE as any] as any).selectedBuisinessRule
      
      // Revert frontend-safe format -> backend format (remove inner quotes)
      if (aide.accessoryProductRule.rule?.childBusinessRuleItem?.length) {
        aide.accessoryProductRule.rule.childBusinessRuleItem.forEach((item: any) => {
          if (item?.childBusinessRuleResult?.length) {
            item.childBusinessRuleResult.forEach((resultObj: any) => {
              let res: string | undefined = resultObj?.result?.trim();

              // Match strings like: return '(...)'; 
              const quotedReturnRegex = /^return\s*['"]\(([\s\S]*?)\);['"]\s*$/i;

              if (res && quotedReturnRegex.test(res)) {
                // Extract the expression inside the quotes
                const match = res.match(quotedReturnRegex);
                if (match && match[1] !== undefined) {
                  const innerExpr = match[1].trim();
                  //  Rebuild correct backend format (no quotes)
                  resultObj.result = `return (${innerExpr.replace(
                    /param(?!.orderInfo)/g,
                    "param.orderInfo"
                  )});`;
                }
              }
            });
          }
        });
      }


        aide.accessoryProductRule = filterKeys(aide.accessoryProductRule, ['rule', 'ruleEngineId']);
    }
    return await saveAideProduct(aide, daaqResourceUid)
}

/**
 * Transforms a business rule item into a subsidy product payload
 */
const transformToPayload = (
  item: any,
  validityFrom: string = '01/12/2025',
  validityUntil: string = '31/12/2025',
  reference: string = ''
): any => {
  return {
    systemUid: 'odm-product',
    objectType: 'odm.product.productfeature.product.accessoryproduct.subsidyproduct',
    daaq: '/',
    reference: reference || item.metadata?.codeRemise || '',
    name: item.metadata?.description || item.label,
    validity: {
      from: item.metadata ? item.metadata.validity_from : validityFrom,
      until: item.metadata ? item.metadata.validity_until : validityUntil
    },
    status: {
      objectType: 'odm.product.productstatus',
      systemUid: 'odm-product',
      resourceUid: 'USE'
    },
    accessoryProductRule: {
      ruleEngineId: null,
      rule: {
        label: item.label,
        externalId: item.externalId,
        failIfNoResult: item.failIfNoResult,
        childInputHeader: item.childInputHeader,
        childOutputHeader: item.childOutputHeader,
        childBusinessRuleItem: item.childBusinessRuleItem,
        returnMultipleResults: item.returnMultipleResults
      }
    }
  };
};

/**
 * Batch saves multiple subsidy products in one shot using Promise.all
 */
export const batchSaveSubsidyProducts = async (
  businessRuleItems: any[],
  options?: {
    validityFrom?: string;
    validityUntil?: string;
    daaqResourceUid?: string;
  }
): Promise<any> => {
  const { validityFrom, validityUntil, daaqResourceUid } = options || {};

  // Transform all items to payloads
  const payloads = businessRuleItems.map(item =>
    transformToPayload(item, validityFrom, validityUntil)
  );

  // Create all save promises
  const savePromises = payloads.map((payload, index) =>
    saveAideProduct(payload, daaqResourceUid)
      .then((response:any) => ({ success: true, response, index }))
      .catch((error:any) => ({
        success: false,
        error,
        index,
        item: businessRuleItems[index]
      }))
  );

  // Execute all saves in parallel
  const results = await Promise.all(savePromises);

  // Separate successes and failures
  const successes = results
    .filter((r:any) => r.success)
    .map((r: any) => r.response);

  const failures = results
    .filter((r: any) => !r.success)
    .map((r: any) => ({
      item: r.item!,
      error: r.error as Error
    }));

  return { successes, failures };
};



export const saveAideProduct = async (
  payload: any,
  daaqResourceUid?: string
) => {
  const systemUid = UON.SystemUid.odmProduct;
    const path = Paths.subsidyproduct;
  const { response, error } = await useApi(systemUid, path).upsert(
    payload,
    daaqResourceUid
  );

  if (error)
    throw new Error(
      `Error in save subsidyproduct with payload ${JSON.stringify(payload)}`
    );

  return response;
}; 

export const getAide = async (id: string): Promise<any> => {
  const { response } = await useApi(UON.SystemUid.odmProduct, Paths.subsidyproduct).get(id)
  const aide = buildAideRule(response)
  
  return aide
};

export const buildAideRule = async (aide: any) => {
 
  if (aide.accessoryProductRule.rule) {
        

        (store.state.queryBuilderModule.queryBuilder[ScaleBuisinessRuleSection.AIDE as any] as any).selectedBuisinessRule = aide.accessoryProductRule.rule
        
    const extracRule = extract(aide.accessoryProductRule.rule, aideResults);
    store.dispatch("queryBuilderModule/setbrResluts", { extracRule: extracRule, currentSection: ScaleBuisinessRuleSection.AIDE });

    aide.aideResults = extracRule || {}
    }
 
    return aide
}